import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, comments, categories, siteInfo } from '@/mocks/data';
import { articleContent } from '@/mocks/articleContent';
import {
  generateBreadcrumbSchema,
  generateArticleSchema,
  generateWebPageSchema,
} from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const content = slug ? articleContent[slug] : '';

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const articleComments = comments.filter((c) => c.articleId === article.id);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  const pageUrl = `${siteInfo.url}/article/${article.slug}`;
  const breadcrumbItems = [
    { name: '首页', url: siteInfo.url },
    { name: category?.name || '分类', url: `${siteInfo.url}/category/${article.category}` },
    { name: article.title, url: pageUrl },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);
  const articleSchema = generateArticleSchema({
    title: article.title,
    description: article.excerpt,
    url: pageUrl,
    datePublished: article.date,
    dateModified: article.date,
    authorName: siteInfo.name,
    imageUrl: article.imageUrl,
  });
  const webPageSchema = generateWebPageSchema(article.title, article.excerpt, pageUrl);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          {/* Breadcrumb + Header */}
          <div className="w-full px-4 md:px-6 py-6 bg-background-100">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-3xl mx-auto">
              {/* Article Header */}
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {category?.name || '吃瓜'}
                  </span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000 ? `${Math.floor(article.views / 10000)}万` : article.views} 浏览
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i> {article.comments} 评论
                  </span>
                </div>
              </div>

              {/* Featured Image */}
              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content }}
              />

              {/* Disclaimer */}
              <div className="mt-10 p-4 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-xs text-foreground-500">
                  <strong>免责声明：</strong>本文内容来源于网络公开信息整理，仅供读者参考，不构成事实认定。如涉及侵权或不当内容，请联系我们进行修改或删除。理性吃瓜，文明上网。
                </p>
              </div>
            </div>
          </article>

          {/* Comments Section */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                网友评论 ({articleComments.length})
              </h2>
              <div className="flex flex-col gap-4">
                {articleComments.map((comment) => (
                  <div key={comment.id} className="p-4 md:p-5 rounded-xl bg-background-50">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={comment.avatar}
                        alt={comment.userName}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{comment.userName}</p>
                        <p className="text-xs text-foreground-500">{comment.date}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                    <div className="flex items-center gap-1 mt-3 text-xs text-foreground-400">
                      <i className="ri-thumb-up-line"></i>
                      <span>{comment.likes}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 bg-background-50">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group block rounded-xl bg-background-100 hover:bg-background-200/70 transition-all overflow-hidden cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-bold font-heading text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}